import React, { useState, useRef } from 'react';
import {
  FaInstagram,
  FaFacebook,
  FaPinterest,
  FaGoogle,
} from 'react-icons/fa';
import ActiveNavLink from './ActiveNavLink';
import { TextInput } from './TextInput';
import { useOrderRoute } from '../contexts/OrderContext';

const { URL } = import.meta.env;

function Footer() {
  const orderRoute = useOrderRoute();
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    setError(null);
    const form = e.currentTarget;
    const formData = new FormData(form);
    const email = formData.get('email');
    try {
      const res = await fetch(`/.netlify/functions/joinEmailList`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error('Failed to join');
      setStatus('success');
      timerRef.current = setTimeout(() => setStatus('idle'), 5000);
    } catch (err) {
      setStatus('error');
      setError('Could not join. Please try again.');
    }
  };

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <>
      <section className="bg-[#234048] text-white py-10">
        <div className="container mx-auto px-4 text-center max-w-xl">
          <h2 className="text-3xl font-medium mb-5" style={{ letterSpacing: '0.01em' }}>
            Join us on our journey
          </h2>
          <p className="mb-6 text-base leading-tight opacity-90">
            We're determined to get a bidet in every bathroom in New York. Follow along on the journey and discover what we discover.
          </p>
          {status === 'success' ? (
            <div className="text-xl font-semibold text-yellow-400 transition-opacity duration-500">Joined!</div>
          ) : (
            <form className="flex flex-col items-center sm:flex-row items-around gap-3 justify-center" onSubmit={handleSubmit}>
              <label htmlFor="footer-email" className="sr-only">Email</label>
              <TextInput
                id="footer-email"
                name="email"
                type="email"
                required
                placeholder="Email"
                className="px-4 py-2 rounded border border-gray-300 text-center bg-white text-gray-900 focus:outline-none w-full"
                aria-label="Email"
                containerClassName="max-w-[300px]"
                disabled={status === 'submitting'}
              />
              <button
                type="submit"
                className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold px-6 py-2 rounded transition-colors max-w-[200px] w-full"
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? 'Joining...' : 'Join'}
              </button>
            </form>
          )}
          {status === 'error' && (
            <div className="text-white mt-4 text-sm">{error}</div>
          )}
        </div>
      </section>
      <footer className="bg-gray-800 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            <div>
              <h3 className="font-bold text-lg mb-4">About</h3>
              <ul className="space-y-2">
                <li>
                  <ActiveNavLink to="/">
                    Home
                  </ActiveNavLink>
                </li>
                <li>
                  <ActiveNavLink to="/about">
                    About Us
                  </ActiveNavLink>
                </li>
                <li>
                  <ActiveNavLink to="/pricing">
                    Pricing
                  </ActiveNavLink>
                </li>
                <li>
                  <ActiveNavLink to="/careers">
                    Careers
                  </ActiveNavLink>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Learn</h3>
              <ul className="space-y-2">
                <li>
                  <ActiveNavLink to="/questions">
                    Frequently Asked Questions
                  </ActiveNavLink>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Products</h3>
              <ul className="space-y-2">
                <li>
                  <ActiveNavLink to="/bidets/recommended">
                    Recommended Bidets
                  </ActiveNavLink>
                </li>
                <li>
                  <ActiveNavLink to={orderRoute}>
                    Order Now
                  </ActiveNavLink>
                </li>
                <li>
                  <ActiveNavLink to="/survey">
                    Get 25% Off
                  </ActiveNavLink>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <ActiveNavLink to="/terms">
                    Terms of Service
                  </ActiveNavLink>
                </li>
                <li>
                  <ActiveNavLink to="/privacy">
                    Privacy Policy
                  </ActiveNavLink>
                </li>
                <li>
                  <ActiveNavLink to="/complaint">
                    File a Complaint
                  </ActiveNavLink>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Connect</h3>
              <div className="flex space-x-4">
                {/* <a href="https://twitter.com/bidetny" className="hover:text-gray-300" target="_blank" rel="noopener noreferrer"><FaTwitter size={24} /></a> */}


                

                <a
                  href="https://facebook.com/bidetny"
                  className="hover:text-gray-300"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                    <FaFacebook size={24} />
                </a>
                <a
                  href="https://instagram.com/bidetny"
                  className="hover:text-gray-300"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaInstagram size={24} />
                </a>
                <a
                  href="https://pinterest.com/bidetny"
                  className="hover:text-gray-300"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FaPinterest size={24} />
                </a>
                <a
                  href="https://g.co/kgs/GVSdYjh"
                  className="hover:text-gray-300"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                    <FaGoogle size={24} />
                </a>
                {/* <a href="https://linkedin.com/company/bidetny" className="hover:text-gray-300" target="_blank" rel="noopener noreferrer"><FaLinkedin size={24} /></a> */}
              </div>
              <div className="mt-4">
                <p className="text-md">
                  Email:{' '}
                  <a
                    href="mailto:support@bidetny.com"
                    className="text-gray-300 hover:underline"
                  >
                    support@bidetny.com
                  </a>
                </p>
              </div>
              <div className="mt-4">
                <p className="text-md">
                  * If you have any impairment under the Americans with Disabilities Act (ADA) or
                  similar law, please contact us for accommodations related to use of this website.
                </p>
              </div>
            </div>
          </div>
          <div className="mt-8 text-center border-t border-gray-700 pt-8">
            <p>
              Made with{' '}
              <span role="img" aria-label="yellow heart">❤️</span>
              {' '}
              in NYC
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}

export default Footer;
